import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Divider,
  Label,
  NumberValueInput,
  Select,
  type SelectOptionType,
  space,
} from 'folio-common-components';
import { formatters, numbers } from 'folio-common-utils';
import * as React from 'react';
import { XForNoIcon } from '../../icons';
import { nameFallback } from '../../utils';
import { SimplePie } from '../SimplePie';
import { Box } from '../box';
import { TextButton } from '../buttons';
import type { Owner } from './';

interface OwnershipPercentageOption extends SelectOptionType {
  value: number;
}

interface Props {
  owner: Owner;
  singleOwner: boolean;
  undistributedCapital: number; // the capital that has not yet been assigned to anyone
  onDelete: () => void;
  onEdit: () => void;
  onOwnershipPercentageChange: (percentage: number) => void;
  onEquityChange: (equity: string) => void;
  percentOffset?: number;
}

const Description = styled.p`
  margin: 0;
`;

const Equity = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  grid-column-gap: 1em;
  column-gap: 1em;
`;

// Patched options will also have non-breaking spaces (\u{A0}) in labels
const ownershipPercentageOptions: OwnershipPercentageOption[] = [
  { value: 10, label: '10\u{A0}%' },
  { value: 20, label: '20\u{A0}%' },
  { value: 25, label: '25\u{A0}%' },
  { value: 30, label: '30\u{A0}%' },
  { value: Number(numbers.toSafePrecision((1 / 3) * 100)), label: '⅓' },
  { value: 40, label: '40\u{A0}%' },
  { value: 50, label: '50\u{A0}%' },
  { value: 60, label: '60\u{A0}%' },
  { value: Number(numbers.toSafePrecision((2 / 3) * 100)), label: '⅔' },
  { value: 70, label: '70\u{A0}%' },
  { value: 75, label: '75\u{A0}%' },
  { value: 80, label: '80\u{A0}%' },
  { value: 90, label: '90\u{A0}%' },
  { value: 100, label: '100\u{A0}%' },
];

// Value of -1 (as opposed to for example 0) prevents the option from being
// automatically selected when the investment is edited manually
const emptyDisabledOption: OwnershipPercentageOption = {
  value: -1,
  label: '',
  disabled: true,
};

type SelectedOwnershipPercentageOptionResult = {
  option: OwnershipPercentageOption;
  patched: boolean;
};

function selectedOwnershipPercentageOption(
  ownershipPercentage: number,
): SelectedOwnershipPercentageOptionResult {
  const existingOptionMatchingFraction = ownershipPercentageOptions.find(
    ({ value }) => value === ownershipPercentage,
  );

  if (existingOptionMatchingFraction != null) {
    return { option: existingOptionMatchingFraction, patched: false };
  }

  const patchedOption = {
    value: Number(numbers.toSafePrecision(ownershipPercentage)),
    label: formatters.formatPercentage(ownershipPercentage),
    disabled: ownershipPercentage === 0 || ownershipPercentage > 100, // override calculated disabling
  };

  return { option: patchedOption, patched: true };
}

function isOwnershipPercentageOptionDisabled(
  option: OwnershipPercentageOption,
  capital: number,
): boolean {
  if (option.value % 10 === 0) {
    return capital % 10 > 0;
  }

  if (option.value % 25 === 0) {
    return capital % 4 > 0;
  }

  return capital % 3 > 0;
}

type GetTheRestOptionArguments = {
  undistributedCapital: number;
  capital: number; // the total capital for the company
  equity: number; // this owner's assigned ownership
  existingOptions: OwnershipPercentageOption[];
};

function getTheRestOption({
  undistributedCapital,
  capital,
  equity,
  existingOptions,
}: GetTheRestOptionArguments): OwnershipPercentageOption | null {
  // Everything is assigned perfectly.
  if (undistributedCapital === 0) {
    return null;
  }

  const availableCapital = undistributedCapital + equity;

  // Too much has already been assigned to other owners.
  if (availableCapital <= 0) {
    return null;
  }

  // Nothing has been assigned to other owners.
  if (availableCapital === capital) {
    return null;
  }

  const remainingAsPercent = Number(
    numbers.toSafePrecision((availableCapital / capital) * 100),
  );

  // Reuse the label if the remaining percentage already exists as a predefined option
  let label: string;

  const existingOption = existingOptions.find(
    ({ value }) => value === remainingAsPercent,
  );

  if (existingOption == null) {
    // Just show it as percent
    label = formatters.formatPercentage(remainingAsPercent);
  } else {
    label = existingOption.label;
  }

  return {
    value: remainingAsPercent,
    label: `Resten (${label})`,
    disabled: false, // the rest should never be disabled
  };
}

const ShareLabel = styled(Label)`
  padding: 8px 0;
`;

export const OwnerSummary: React.FC<Props> = props => {
  const {
    owner,
    singleOwner,
    onDelete,
    percentOffset,
    onOwnershipPercentageChange,
    onEquityChange,
    onEdit,
    undistributedCapital,
  } = props;
  const ownershipPercentage = Number(
    numbers.toSafePrecision(owner.fraction * 100),
  );
  const selectedOwnershipPercentageOptionResult =
    selectedOwnershipPercentageOption(ownershipPercentage);

  const specialOwnershipPercentageOptions: OwnershipPercentageOption[] = [];

  // If there's only a single owner, 100 % is a valid choice by default
  if (singleOwner) {
    if (selectedOwnershipPercentageOptionResult.option.value !== 100) {
      specialOwnershipPercentageOptions.push({
        value: 100,
        label: `Alt (${formatters.formatPercentage(100)})`,
      });
    }
  } else {
    // The distribution is imperfect, so we should patch in "the rest".
    const theRestOption = getTheRestOption({
      undistributedCapital,
      capital: owner.capital,
      equity: owner.equity,
      existingOptions: ownershipPercentageOptions,
    });

    if (theRestOption !== null) {
      // Patch in "the rest", which should never be disabled
      specialOwnershipPercentageOptions.push(theRestOption);
    }
  }

  // Patch in the selected option if it doesn't already exist
  if (selectedOwnershipPercentageOptionResult.patched) {
    specialOwnershipPercentageOptions.push(
      selectedOwnershipPercentageOptionResult.option,
    );
  }

  // Visually separate any special options from the rest with a blank option
  if (specialOwnershipPercentageOptions.length > 0) {
    specialOwnershipPercentageOptions.push(emptyDisabledOption);
  }

  const allOwnershipPercentageOptions: OwnershipPercentageOption[] = [
    ...specialOwnershipPercentageOptions,
    ...ownershipPercentageOptions,
  ];

  const shareId = React.useId();
  const capitalId = React.useId();

  return (
    <Box data-testid="OwnerItem">
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          ${space([16], 'gap')};
        `}
      >
        Eier
        <TextButton
          onClick={onDelete}
          level="destructive"
          icon={
            <XForNoIcon
              css={css`
                margin-top: -2px;
              `}
            />
          }
        >
          Fjern
        </TextButton>
      </div>

      <Divider css={space([16], 'margin-vertical')} />

      <div>
        <b css={space([8], 'margin-right')}>{nameFallback(owner.item.name)}</b>{' '}
        <TextButton onClick={onEdit}>Rediger info</TextButton>
      </div>
      {owner.kind === 'company' && (
        <Description css={space([4], 'margin-top')}>
          Ved{' '}
          {formatters.joinRunningTextList(owner.signatories.map(e => e.name))}
        </Description>
      )}

      <Equity data-testid="owner-item-equity">
        <div>
          <ShareLabel for={shareId}>Andel</ShareLabel>
          <Select
            id={shareId}
            css={css`
              width: 100%;
            `}
            onChange={option => onOwnershipPercentageChange(option.value)}
            selectedOption={selectedOwnershipPercentageOptionResult.option}
            options={allOwnershipPercentageOptions}
            isOptionDisabled={option =>
              isOwnershipPercentageOptionDisabled(option, owner.capital)
            }
            icon={
              <SimplePie
                size={28}
                offset={percentOffset}
                value={owner.fraction}
                backgroundColor="#f5f5f5"
                sliceColor="#4b33dd"
              />
            }
          />
        </div>
        <div>
          <ShareLabel for={capitalId}>Investering</ShareLabel>
          <NumberValueInput
            id={capitalId}
            value={owner.equityAsString}
            onChange={equity => {
              onEquityChange(equity.replace(/\s/g, ''));
            }}
            message={
              owner.equity > owner.capital || owner.equity < 1
                ? { kind: 'error' }
                : undefined
            }
            autoComplete="off"
            rightContent={{ content: 'kr', width: 13 }}
          />
        </div>
      </Equity>
    </Box>
  );
};
